import { lazy, Suspense, useEffect, useRef } from "react"
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Outlet } from "react-router-dom"
import { AuthProvider, useAuth } from "@/components/auth-context"
import { TimeTrackingProvider } from "@/hooks/use-time-tracking"
import { ClientsProvider } from "@/components/clients-context"
import { TimerSettingsProvider } from "@/hooks/use-timer-settings.tsx"
import { ThemeProvider } from "@/components/theme-provider"
import Layout from "@/components/layout/Layout"
import { Loader2 } from "lucide-react"
import { supabase } from "@/lib/supabase"
import { Toaster, toast } from "sonner"
import { useAppVersion } from "@/hooks/use-app-version"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { queryClient } from "@/lib/query-client"
import * as Sentry from "@sentry/react"
import { useTranslation } from "react-i18next"
// Lazy Loading Pages
const Login = lazy(() => import("@/pages/Login"))
const Register = lazy(() => import("@/pages/Register"))
const Dashboard = lazy(() => import("@/pages/Dashboard"))
const History = lazy(() => import("@/pages/History"))
const Profile = lazy(() => import("@/pages/Profile"))
const Reports = lazy(() => import("@/pages/Reports"))
const Projects = lazy(() => import("@/pages/Projects"))
const Clients = lazy(() => import("@/pages/Clients"))
const Quotes = lazy(() => import("@/pages/Quotes"))
const ClientDetails = lazy(() => import("@/pages/ClientDetails"))
const Team = lazy(() => import("@/pages/Team"))
const ProjectDetails = lazy(() => import("@/pages/ProjectDetails"))
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"))
const Help = lazy(() => import("@/pages/Help"))
const HelpCategory = lazy(() => import("@/pages/HelpCategory"))
const HelpArticle = lazy(() => import("@/pages/HelpArticle"))
const UpdatePassword = lazy(() => import("@/pages/UpdatePassword"))
const AccountDeleted = lazy(() => import("@/pages/AccountDeleted"))
const Changelog = lazy(() => import("@/pages/Changelog"))
const BillingPage = lazy(() => import("@/pages/BillingPage"))
const AiAssistant = lazy(() => import("@/pages/AiAssistant"))
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"))
const PublicQuote = lazy(() => import("@/pages/PublicQuote"))
const Landing = lazy(() => import("@/pages/Landing"))
const LegalPage = lazy(() => import("@/pages/LegalPage"))
const EmailConfirmed = lazy(() => import("@/pages/EmailConfirmed"))
const Approvals = lazy(() => import("@/pages/Approvals"))
const PublicPortal = lazy(() => import("@/pages/PublicPortal"))

import { isLandingDomain, getHomePath } from '@/lib/domain-logic';
const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.tempora.lat';


function LoadingFallback() {
  return (
    <div className="h-screen w-full flex flex-col items-center justify-center gap-4 bg-background text-foreground">
      <div className="relative">
        <div className="h-16 w-16 bg-primary rounded-2xl flex items-center justify-center animate-pulse">
          <Loader2 className="h-8 w-8 animate-spin text-primary-foreground" />
        </div>
        <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-xl animate-pulse" />
      </div>
      <div className="flex flex-col items-center gap-2">
        <p className="text-lg font-semibold animate-pulse">tempora</p>
        <p className="text-sm text-muted-foreground">Cargando...</p>
      </div>
    </div>
  )
}

function ExternalRedirect({ url }: { url: string }) {
  useEffect(() => {
    window.location.href = url;
  }, [url]);

  return <LoadingFallback />;
}

function ProtectedRoute({ withLayout = true }: { withLayout?: boolean }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingFallback />
  }

  // If user is logged in but on the landing domain, redirect them to the app subdomain
  // to ensure session persistence and consistent routing.
  if (user && isLandingDomain) {
    const path = window.location.pathname;
    return <ExternalRedirect url={`${APP_URL}${path}${window.location.search}`} />;
  }

  if (!user) {
    if (window.location.hash.includes('type=recovery')) {
      return <LoadingFallback />
    }
    return <Navigate to="/login" replace />
  }

  return withLayout ? <Layout /> : <Outlet />
}

function LandingRoute() {
  const { user, loading } = useAuth()
  
  if (loading) return <LoadingFallback />
  
  // If user is already logged in on the landing page domain, redirect to app subdomain
  if (user) {
    // Force redirect to the app domain
    return <ExternalRedirect url={`${APP_URL}/dashboard`} />;
  }
  
  return <Landing />
}

function DomainRootRoute() {
  // Always render the Landing page on the landing domain (tempora.lat)
  if (isLandingDomain) {
    return <LandingRoute />
  }
  
  // For the app subdomain (app.tempora.lat), the root path `/` redirects to the Dashboard.
  // The Dashboard is protected inside the Layout Route.
  return <Navigate to="/dashboard" replace />
}

function AuthRouteRedirect({ children }: { children: React.ReactNode }) {
  if (isLandingDomain) {
    const path = window.location.pathname;
    return <ExternalRedirect url={`${APP_URL}${path}${window.location.search}`} />;
  }
  return <>{children}</>;
}

function AppInitializer() {
  const navigate = useNavigate()
  const notifiedVersion = useRef<string | null>(null)

  // Hook para detectar cambios de versión
  const { hasUpdate, updateInfo, currentVersion } = useAppVersion()

  useEffect(() => {
    if (hasUpdate && updateInfo && notifiedVersion.current !== currentVersion) {
      notifiedVersion.current = currentVersion
      toast.success(`🚀 Nueva versión ${currentVersion} disponible`, {
        description: updateInfo.title,
        action: {
          label: 'Ver cambios',
          onClick: () => navigate(`/changelog/${currentVersion}`)
        },
        duration: 8000
      })
    }
  }, [hasUpdate, updateInfo, currentVersion, navigate])

  useEffect(() => {
    // Escuchar eventos de recuperación de contraseña de Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && window.location.pathname !== '/update-password') {
        navigate('/update-password', { replace: true })
      }
    })

    // Caso de borde: Si entramos con el hash pero el evento no se dispara a tiempo
    if (window.location.hash.includes('type=recovery') && window.location.pathname !== '/update-password') {
      navigate('/update-password', { replace: true })
    }

    return () => subscription.unsubscribe()
  }, [navigate])

  return null
}

export default function App() {
  const { i18n } = useTranslation()

  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <AuthProvider>
          <ClientsProvider>
            <TimerSettingsProvider>
              <TimeTrackingProvider>
                <Router>
                  <AppInitializer />
                  <Sentry.ErrorBoundary
                    fallback={({ resetError }) => (
                      <div className="flex flex-col items-center justify-center p-8 bg-card rounded-xl shadow-lg border text-center my-auto mx-auto max-w-md mt-20">
                        <h2 className="text-xl font-bold mb-2">¡Ups! Algo salió mal</h2>
                        <p className="text-muted-foreground text-sm mb-4">
                          Nuestro equipo ya ha sido notificado del error.
                        </p>
                        <button
                          onClick={resetError}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-md font-medium text-sm transition-colors"
                        >
                          Intentar de nuevo
                        </button>
                      </div>
                    )}
                  >
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        {/* Public landing route handled by a wrapper to avoid React Router conditioning bugs */}
                        <Route path="/" element={<DomainRootRoute />} />
                        <Route path="/landing" element={<Landing />} />
                        <Route path="/legal/:docId" element={<LegalPage />} />
                        <Route path="/login" element={<AuthRouteRedirect><Login /></AuthRouteRedirect>} />
                        <Route path="/register" element={<AuthRouteRedirect><Register /></AuthRouteRedirect>} />
                        <Route path="/forgot-password" element={<AuthRouteRedirect><ForgotPassword /></AuthRouteRedirect>} />
                        <Route path="/update-password" element={<UpdatePassword />} />
                        <Route path="/account-deleted" element={<AccountDeleted />} />
                        <Route path="/email-confirmed" element={<EmailConfirmed />} />
                        <Route path="/demo" element={<Dashboard />} />
                        
                        {/* Public but dynamic unauthenticated paths */}
                        <Route path="/public-portal" element={<PublicPortal />} />
                        <Route path="/q/:id" element={<PublicQuote />} />
                        {/* Email invitation acceptance — public but handles auth internally */}
                        <Route path="/invite/:token" element={<AcceptInvite />} />

                        {/* Protected routes with persistent layout */}
                        <Route element={<ProtectedRoute />}>
                          {/* Make dashboard available on both paths to be 100% safe against redirect domain issues */}
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/history" element={<History />} />
                          <Route path="/profile" element={<Profile />} />
                          <Route path="/reports" element={<Reports />} />
                          <Route path="/projects" element={<Projects />} />
                          <Route path="/projects/:id" element={<ProjectDetails />} />
                          <Route path="/clients" element={<Clients />} />
                          <Route path="/quotes" element={<Quotes />} />
                          <Route path="/clients/:id" element={<ClientDetails />} />
                          <Route path="/team" element={<Team />} />
                          <Route path="/help" element={<Help />} />
                          <Route path="/help/:category" element={<HelpCategory />} />
                          <Route path="/help/:category/:articleId" element={<HelpArticle />} />
                          <Route path="/changelog/:version" element={<Changelog />} />
                          <Route path="/billing" element={<BillingPage />} />
                          <Route path="/ai-assistant" element={<AiAssistant />} />
                          <Route path="/approvals" element={<Approvals />} />
                        </Route>

                        <Route path="*" element={<Navigate to={getHomePath()} replace />} />
                    </Routes>
                  </Suspense>
                </Sentry.ErrorBoundary>
              </Router>
            </TimeTrackingProvider>
          </TimerSettingsProvider>
        </ClientsProvider>
        <Toaster position="top-right" expand={true} richColors />
      </AuthProvider>
    </ThemeProvider>
    <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
  </QueryClientProvider >
)
}
